import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Field } from '../../../components/Field';
import { CHART_SERIES } from '../../../lib/chartColors';
import { cn } from '../../../lib/utils';
import { useShiftStore, type ShiftType } from '../../../store/shiftStore';
import { formatHours, shiftHours } from './rosterDates';

/** Shift colours are roster data (stored per type); pick from the chart palette. */
const SHIFT_COLORS = CHART_SERIES as readonly string[];

const EMPTY = { name: '', startTime: '09:00', endTime: '18:00', color: SHIFT_COLORS[0] };

export function ShiftTypeDialog({ open, onOpenChange, editing }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when editing an existing type. */
  editing: ShiftType | null;
}) {
  const { shifts, createShift, updateShift } = useShiftStore();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(editing
      ? { name: editing.name, startTime: editing.startTime, endTime: editing.endTime, color: editing.color }
      : EMPTY);
  }, [open, editing]);

  const hours = form.startTime && form.endTime ? shiftHours(form.startTime, form.endTime) : 0;
  const overnight = !!form.startTime && !!form.endTime && form.endTime <= form.startTime;

  const validate = () => {
    const next: Record<string, string> = {};
    const name = form.name.trim();
    if (!name) next.name = 'Name is required';
    else if (shifts.some((s) => s.id !== editing?.id && s.name.toLowerCase() === name.toLowerCase())) {
      next.name = 'A shift with this name already exists';
    }
    if (!form.startTime) next.startTime = 'Start time is required';
    if (!form.endTime) next.endTime = 'End time is required';
    if (form.startTime && form.startTime === form.endTime) next.endTime = 'End time must differ from start time';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = { name: form.name.trim(), startTime: form.startTime, endTime: form.endTime, color: form.color };
    try {
      if (editing) {
        await updateShift(editing.id, payload);
        toast.success(`${payload.name} updated`);
      } else {
        await createShift(payload);
        toast.success(`${payload.name} shift created`);
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit shift type' : 'New shift type'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Changes apply to shifts from today onwards; past days keep their recorded hours.'
              : 'A named shift with fixed working hours that you can assign on the roster.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Name" htmlFor="shift-name" required error={errors.name}>
            <Input
              id="shift-name"
              value={form.name}
              maxLength={60}
              placeholder="e.g. General, Night"
              invalid={!!errors.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Starts" htmlFor="shift-start" required error={errors.startTime}>
              <Input id="shift-start" type="time" value={form.startTime} invalid={!!errors.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
            </Field>
            <Field label="Ends" htmlFor="shift-end" required error={errors.endTime}>
              <Input id="shift-end" type="time" value={form.endTime} invalid={!!errors.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
            </Field>
          </div>
          {hours > 0 && form.startTime !== form.endTime && (
            <p className="text-sm text-muted-foreground">
              {formatHours(hours)} per shift{overnight ? ' · ends the next day' : ''}
            </p>
          )}
          <Field label="Colour">
            <div className="flex flex-wrap gap-2">
              {SHIFT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  aria-pressed={form.color.toLowerCase() === c.toLowerCase()}
                  onClick={() => setForm({ ...form, color: c })}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-md border-2 transition-transform',
                    form.color.toLowerCase() === c.toLowerCase() ? 'border-foreground' : 'border-transparent hover:scale-105'
                  )}
                  style={{ backgroundColor: c }}
                >
                  {form.color.toLowerCase() === c.toLowerCase() && <Check className="size-4 text-white" />}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} isLoading={saving}>{editing ? 'Save changes' : 'Create shift'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
